import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { PreciosImportPreviewRow, resumirPreciosImportPreview } from '../../utils/buildPreciosImportPreview';
import { PreciosImportPreviewColumnDef } from '../../utils/preciosImportPreviewColumns';
import {
  formatPrecioImport,
  formatPrecioImportInput,
  parsePrecioImportInput,
} from '../../utils/preciosImportExcelParse';

type FiltroTab = 'todos' | 'actualizar' | 'no_encontrado' | 'otros';

interface Props {
  open: boolean;
  onClose: () => void;
  filas: PreciosImportPreviewRow[];
  /** Definición dinámica: cada par actual / Excel según columnas mapeadas en el formulario. */
  columnas: PreciosImportPreviewColumnDef[];
  onFilasChange?: (filas: PreciosImportPreviewRow[]) => void;
  titulo?: string;
  subtitulo?: string;
  mostrarDescripcion?: boolean;
  editable?: boolean;
  puedeEjecutarDesdePreview?: boolean;
  procesado?: boolean;
  onReSimular?: () => void | Promise<void>;
  onProcesar?: () => void | Promise<void>;
  ejecutando?: boolean;
}

const formatMoney = formatPrecioImport;
const formatInputNumber = formatPrecioImportInput;
const parseInputNumber = parsePrecioImportInput;

const estadoLabelPendiente: Record<PreciosImportPreviewRow['estado'], string> = {
  actualizar: 'Se actualizará',
  no_encontrado: 'No encontrado',
  invalido: 'Inválido',
  sin_datos: 'Sin datos',
};

const estadoLabelProcesado: Record<PreciosImportPreviewRow['estado'], string> = {
  actualizar: 'Actualizado',
  no_encontrado: 'No encontrado',
  invalido: 'Inválido',
  sin_datos: 'Sin datos',
};

const estadoColor: Record<PreciosImportPreviewRow['estado'], 'success' | 'error' | 'warning' | 'default'> = {
  actualizar: 'success',
  no_encontrado: 'error',
  invalido: 'warning',
  sin_datos: 'default',
};

const colNumeroSx = { minWidth: 96, maxWidth: 120 };
const colEditableSx = { minWidth: 108, maxWidth: 140 };
const colDescripcionSx = { minWidth: 140, maxWidth: 220 };

const cellDetalleSx = {
  minWidth: 200,
  maxWidth: 320,
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
  lineHeight: 1.35,
  verticalAlign: 'top' as const,
};

const celdaTruncadaSx = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

const inputEditableSx = {
  '& .MuiInputBase-input': {
    fontSize: '0.75rem',
    py: 0.35,
    px: 0.5,
    textAlign: 'right' as const,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
  },
};

const CeldaTextoTruncado: React.FC<{ texto: string }> = ({ texto }) => {
  const display = texto || '—';
  const contenido = (
    <Box component="span" sx={{ ...celdaTruncadaSx, whiteSpace: 'nowrap' }}>
      {display}
    </Box>
  );
  if (!texto) return contenido;
  return (
    <Tooltip title={texto} placement="top" arrow>
      {contenido}
    </Tooltip>
  );
};

const CeldaDetalle: React.FC<{ mensaje: string }> = ({ mensaje }) => (
  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', lineHeight: 1.35 }}>
    {mensaje || '—'}
  </Typography>
);

const CeldaEditableNumero: React.FC<{
  value: number | null | undefined;
  disabled?: boolean;
  onCommit: (value: number | null) => void;
  destacado?: boolean;
}> = ({ value, disabled, onCommit, destacado }) => {
  const [local, setLocal] = useState(formatInputNumber(value));

  useEffect(() => {
    setLocal(formatInputNumber(value));
  }, [value]);

  if (disabled) {
    return <>{formatMoney(value)}</>;
  }

  return (
    <TextField
      size="small"
      fullWidth
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        const parsed = parseInputNumber(local);
        onCommit(parsed);
        setLocal(formatInputNumber(parsed));
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder="—"
      inputProps={{ inputMode: 'decimal', 'aria-label': 'Valor editable desde Excel' }}
      sx={{
        ...inputEditableSx,
        ...(destacado
          ? (theme) => ({
              '& .MuiOutlinedInput-root': {
                fontWeight: 700,
                color: theme.palette.primary.dark,
                backgroundColor: theme.palette.primary.light + '14',
              },
            })
          : {}),
      }}
    />
  );
};

const PreciosImportPreviewDialog: React.FC<Props> = ({
  open,
  onClose,
  filas,
  columnas,
  onFilasChange,
  titulo = 'Previsualización de actualización de precios',
  subtitulo,
  mostrarDescripcion = false,
  editable = true,
  puedeEjecutarDesdePreview = false,
  procesado = false,
  onReSimular,
  onProcesar,
  ejecutando = false,
}) => {
  const [tab, setTab] = useState<FiltroTab>('todos');
  const resumen = useMemo(() => resumirPreciosImportPreview(filas), [filas]);
  const esEditable = editable && !procesado && !!onFilasChange;
  const estadoLabel = procesado ? estadoLabelProcesado : estadoLabelPendiente;
  const tituloEfectivo =
    titulo ?? (procesado ? 'Detalle de cambios aplicados' : 'Previsualización de actualización de precios');
  const etiquetaActualizados = procesado ? 'Actualizados' : 'Se actualizarán';

  const columnasExcelEditables = useMemo(
    () => columnas.filter((c) => c.editable && c.columnaExcel),
    [columnas]
  );

  const tableMinWidth = useMemo(() => {
    const base = 320 + (mostrarDescripcion ? 160 : 0);
    const porColumna = columnas.reduce((acc, col) => acc + (col.tipo === 'par' ? 200 : 120), 0);
    return base + porColumna;
  }, [columnas, mostrarDescripcion]);

  const filasFiltradas = useMemo(() => {
    switch (tab) {
      case 'actualizar':
        return filas.filter((f) => f.estado === 'actualizar');
      case 'no_encontrado':
        return filas.filter((f) => f.estado === 'no_encontrado');
      case 'otros':
        return filas.filter((f) => f.estado === 'invalido' || f.estado === 'sin_datos');
      default:
        return filas;
    }
  }, [filas, tab]);

  const actualizarFila = (
    filaExcel: number,
    codigo: string,
    updater: (f: PreciosImportPreviewRow) => PreciosImportPreviewRow
  ) => {
    if (!onFilasChange) return;
    onFilasChange(
      filas.map((f) => (f.filaExcel === filaExcel && f.codigo === codigo ? updater(f) : f))
    );
  };

  const filaEsEditable = (fila: PreciosImportPreviewRow) =>
    esEditable && fila.estado === 'actualizar';

  const headerDestacadoSx = (theme: { palette: { primary: { dark: string; light: string } } }) => ({
    fontWeight: 700,
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.light + '22',
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          maxHeight: 'calc(100% - 48px)',
        },
      }}
    >
      <DialogTitle sx={{ color: 'primary.main', fontWeight: 600, pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={600}>
          {tituloEfectivo}
        </Typography>
        {subtitulo && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {subtitulo}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 0 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Total: ${resumen.total}`} size="small" />
          <Chip label={`${etiquetaActualizados}: ${resumen.actualizar}`} color="success" size="small" variant="outlined" />
          <Chip label={`No encontrados: ${resumen.noEncontrado}`} color="error" size="small" variant="outlined" />
          {(resumen.invalido > 0 || resumen.sinDatos > 0) && (
            <Chip
              label={`Otros: ${resumen.invalido + resumen.sinDatos}`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          {resumen.editados > 0 && (
            <Chip label={`Editados: ${resumen.editados}`} color="info" size="small" variant="filled" />
          )}
        </Stack>

        {esEditable && columnasExcelEditables.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Cada concepto muestra el valor <strong>actual del sistema</strong> y el <strong>nuevo desde Excel</strong>.
            Puede editar las columnas marcadas con letra de Excel (
            {columnasExcelEditables.map((c) => c.labelNuevo).join(', ')}). Máx. 2 decimales.
          </Typography>
        )}
        {procesado && columnas.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Los cambios ya fueron aplicados en la base de datos. Cada fila muestra el valor <strong>anterior</strong> y el{' '}
            <strong>valor aplicado desde Excel</strong>.
          </Typography>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`Todos (${resumen.total})`} value="todos" />
          <Tab label={`${etiquetaActualizados} (${resumen.actualizar})`} value="actualizar" />
          <Tab label={`No encontrados (${resumen.noEncontrado})`} value="no_encontrado" />
          <Tab label={`Otros (${resumen.invalido + resumen.sinDatos})`} value="otros" />
        </Tabs>

        {filas.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay filas para mostrar en la previsualización.
          </Typography>
        ) : columnas.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Configure al menos una columna de costo o precio neto en el formulario para ver la previsualización.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', overflowX: 'auto', mt: 1 }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: tableMinWidth,
                tableLayout: 'auto',
                '& .MuiTableCell-root': {
                  fontSize: '0.75rem',
                  py: 0.6,
                  px: 0.75,
                  lineHeight: 1.25,
                },
                '& .MuiTableCell-head': {
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  whiteSpace: 'normal',
                  lineHeight: 1.15,
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ minWidth: 48 }}>Fila</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Código</TableCell>
                  {mostrarDescripcion && <TableCell sx={colDescripcionSx}>Descripción</TableCell>}
                  <TableCell sx={{ minWidth: 120 }}>Estado</TableCell>
                  {columnas.map((col) =>
                    col.tipo === 'par' ? (
                      <React.Fragment key={col.id}>
                        <TableCell align="right" sx={colNumeroSx}>
                          {col.labelActual}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={(theme) => ({
                            ...colEditableSx,
                            ...(col.editable || col.destacado ? headerDestacadoSx(theme) : {}),
                          })}
                        >
                          {col.labelNuevo}
                        </TableCell>
                      </React.Fragment>
                    ) : (
                      <TableCell
                        key={col.id}
                        align="right"
                        sx={(theme) => ({
                          ...colEditableSx,
                          ...headerDestacadoSx(theme),
                        })}
                      >
                        {col.labelNuevo}
                      </TableCell>
                    )
                  )}
                  <TableCell sx={cellDetalleSx}>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filasFiltradas.map((fila) => (
                  <TableRow
                    key={`${fila.filaExcel}-${fila.codigo}`}
                    hover
                    sx={fila.editado ? { bgcolor: 'action.hover' } : undefined}
                  >
                    <TableCell>{fila.filaExcel}</TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CeldaTextoTruncado texto={fila.codigo} />
                        {fila.editado && (
                          <Chip label="Editado" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Stack>
                    </TableCell>
                    {mostrarDescripcion && (
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <CeldaTextoTruncado texto={fila.descripcion ?? ''} />
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip label={estadoLabel[fila.estado]} color={estadoColor[fila.estado]} size="small" />
                    </TableCell>
                    {columnas.map((col) =>
                      col.tipo === 'par' ? (
                        <React.Fragment key={col.id}>
                          <TableCell align="right">{formatMoney(col.getActual(fila))}</TableCell>
                          <TableCell align="right">
                            <CeldaEditableNumero
                              value={col.getNuevo(fila)}
                              disabled={!filaEsEditable(fila) || !col.editable}
                              destacado={col.destacado}
                              onCommit={(v) =>
                                actualizarFila(fila.filaExcel, fila.codigo, (f) => col.setNuevo(f, v))
                              }
                            />
                          </TableCell>
                        </React.Fragment>
                      ) : (
                        <TableCell align="right" key={col.id}>
                          <CeldaEditableNumero
                            value={col.getNuevo(fila)}
                            disabled={!filaEsEditable(fila) || !col.editable}
                            destacado={col.destacado}
                            onCommit={(v) =>
                              actualizarFila(fila.filaExcel, fila.codigo, (f) => col.setNuevo(f, v))
                            }
                          />
                        </TableCell>
                      )
                    )}
                    <TableCell sx={cellDetalleSx}>
                      <CeldaDetalle mensaje={fila.mensaje ?? ''} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {mostrarDescripcion && columnas.some((c) => c.id === 'precioNeto') && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Los valores «nuevos» se calculan con el % de ganancia e IVA del artículo. Si el Excel trae precio
              neto en una columna mapeada, ese valor tiene prioridad y es editable.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={ejecutando}>
          Cerrar
        </Button>
        {puedeEjecutarDesdePreview && onReSimular && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            disabled={ejecutando}
            onClick={() => void onReSimular()}
          >
            Re-simular con cambios
          </Button>
        )}
        {puedeEjecutarDesdePreview && onProcesar && (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={ejecutando}
            onClick={() => void onProcesar()}
          >
            Procesar desde previsualización
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PreciosImportPreviewDialog;
